import {
  Button,
  DataTable,
  Icon,
  PageSection,
  SegmentedControl,
  SegmentedControlItem,
  Spacer,
  Stack,
  TextTitle,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  type TranslationKeys,
} from '@pypestream/translations';
import { ColumnDef } from '@tanstack/react-table';
import { html } from 'lit/static-html.js';
import { FC, useEffect, useState } from 'react';

import {
  useDeleteReportingApiCredentialModal,
  useReportingApiCredentialModal,
} from '../../components/modals';
import { SettingsEmptyState } from '../../components/settings-empty-state';
import { SettingsSectionLoader } from '../../components/settings-section-loader';
import { useLoadingState, useProStudioViewerRole } from '../../hooks';
import {
  getEnvDotColor,
  getEnvDotStyle,
  type ReportingApiCredentialRow,
} from '../../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { PROJECT_SETTINGS_SECTIONS } from './sections';
import { useGoToChannel } from './use-go-to-channel';

const section = PROJECT_SETTINGS_SECTIONS[1];

type Env = 'live' | 'sandbox';

const MAX_VISIBLE_CHANNELS = 4;
const CHANNELS_MAX_WIDTH = 420;

const PILL_STYLE =
  '--ps-tag-bg-color: rgba(98, 105, 124, 0.05); --ps-tag-bg-hover-color: rgba(98, 105, 124, 0.1);';

// The full key is shown only once at creation; the list shows a masked key
// (dots + last 4) so it isn't exposed at rest.
const maskKey = (key: string): string =>
  key.length > 4 ? `${'•'.repeat(8)}${key.slice(-4)}` : key;

export const ReportingApiCredentials: FC = () => {
  const [env, setEnv] = useState<Env>('sandbox');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const apiCredentials = useProStudioCtxSelector((ctx) => ctx.apiCredentials);
  const isViewer = useProStudioViewerRole();

  const { openModal: openCredentialModal, reportingApiCredentialModal } =
    useReportingApiCredentialModal();
  const { openModal: openDeleteModal, deleteReportingApiCredentialModal } =
    useDeleteReportingApiCredentialModal();
  const goToChannel = useGoToChannel();

  // Load once. See live-agent-platforms.tsx for why this is gated on
  // useLoadingState() too, not just hasLoaded.
  const isPageLoading = useLoadingState();
  const hasLoaded = apiCredentials != null;
  useEffect(() => {
    if (!isPageLoading && !hasLoaded) {
      sendProStudioEvent({ type: 'loadApiCredentials' });
    }
  }, [isPageLoading, hasLoaded]);

  const toggleExpanded = (id: string) =>
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const rows: ReportingApiCredentialRow[] = apiCredentials?.[env] ?? [];

  const columns: ColumnDef<ReportingApiCredentialRow>[] = [
    {
      accessorKey: 'name',
      accessorFn: (row) => row.name,
      header: 'Name',
      cell: ({ row }) =>
        html`<div style="min-width: 160px;">
          <ps-text-subtitle size="small">${row.original.name}</ps-text-subtitle>
        </div>`,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) =>
        html`<ps-text-body size="small">
          ${row.original.description || '—'}
        </ps-text-body>`,
    },
    {
      accessorKey: 'channels',
      header: 'Channels',
      cell: ({ row }) => {
        const { channels, id } = row.original;
        const isExpanded = !!expandedRows[id];
        const visible = isExpanded
          ? channels
          : channels.slice(0, MAX_VISIBLE_CHANNELS);
        const remaining = channels.length - MAX_VISIBLE_CHANNELS;
        const wrapStyle = `max-width: ${CHANNELS_MAX_WIDTH}px; display: flex; flex-wrap: wrap; gap: 6px 8px; align-items: center;`;

        if (!channels.length) {
          return html`<ps-text-body size="small">—</ps-text-body>`;
        }

        return html`<div style=${wrapStyle}>
          ${visible.map(
            (channel) =>
              html`<ps-tooltip content="Go to channel">
                <ps-tag
                  size="small"
                  minimal
                  interactive
                  style=${PILL_STYLE}
                  @click=${() => goToChannel(channel)}
                >
                  <span
                    style="display:inline-block;width:8px;height:8px;border-radius:50%;flex:0 0 auto;background:${getEnvDotColor(
                      channel.env
                    )};"
                  ></span>
                  ${channel.name}
                  <ps-icon name="arrow-in-circle" size="xsmall"></ps-icon>
                </ps-tag>
              </ps-tooltip>`
          )}
          ${remaining > 0
            ? html`<ps-tag
                size="small"
                minimal
                interactive
                style=${PILL_STYLE}
                @click=${() => toggleExpanded(id)}
              >
                ${isExpanded ? 'See less' : `See ${remaining} more`}
                <ps-icon
                  name=${isExpanded ? 'chevrons-down-up' : 'chevrons-up-down'}
                  size="xsmall"
                ></ps-icon>
              </ps-tag>`
            : null}
        </div>`;
      },
    },
    {
      accessorKey: 'key',
      header: 'Key',
      cell: ({ row }) =>
        html`<ps-text-body size="small" truncate
          >${maskKey(row.original.key)}</ps-text-body
        >`,
    },
    // Edit/Delete are the only entries, so viewers get no actions column.
    ...(isViewer
      ? []
      : [
          {
            id: 'actions',
            header: () =>
              html`<span style="opacity: 0; font-size: 4px;">Actions</span>`,
            cell: ({ row }) => html`
              <ps-stack justifyContent="end">
                <ps-icon-button
                  name="kebab"
                  size="small"
                  id=${`reporting-api-actions-${row.original.id}`}
                ></ps-icon-button>
              </ps-stack>
              <ps-dropdown
                trigger=${`reporting-api-actions-${row.original.id}`}
                placement="bottom-end"
              >
                <ps-menu>
                  <ps-menu-item
                    @click=${() =>
                      openCredentialModal('edit', {
                        env,
                        credentialId: row.original.id,
                        pypeId: row.original.pypeId,
                      })}
                  >
                    <ps-icon name="edit" size="small" slot="prefix"></ps-icon>
                    Edit
                  </ps-menu-item>
                  <ps-menu-item
                    variant="danger"
                    @click=${() =>
                      openDeleteModal({
                        credentialId: row.original.id,
                        pypeId: row.original.pypeId,
                        credentialName: row.original.name,
                        env,
                      })}
                  >
                    <ps-icon name="delete" size="small" slot="prefix"></ps-icon>
                    Delete
                  </ps-menu-item>
                </ps-menu>
              </ps-dropdown>
            `,
            meta: { align: 'end' },
          } as ColumnDef<ReportingApiCredentialRow>,
        ]),
  ];

  return (
    <PageSection size="small" test-id="ps-reporting-api-credentials-section">
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <TextTitle size="small">
          <TranslationComponent i18nKey={section.i18nKey as TranslationKeys}>
            {section.label}
          </TranslationComponent>
        </TextTitle>

        <Stack alignItems="center" gutter="medium" nowrap>
          <SegmentedControl
            test-id="ps-reporting-api-credentials-env"
            fitContent
            value={env}
            onChange={(e) => setEnv(e.detail.value as Env)}
          >
            <SegmentedControlItem value="sandbox">
              <span
                slot="prefix"
                style={getEnvDotStyle('sandbox', env === 'sandbox')}
              />
              Sandbox
            </SegmentedControlItem>
            <SegmentedControlItem value="live">
              <span
                slot="prefix"
                style={getEnvDotStyle('live', env === 'live')}
              />
              Live
            </SegmentedControlItem>
          </SegmentedControl>

          {!isViewer && (
            <Button
              test-id="ps-reporting-api-credentials-add"
              size="large"
              onClick={() => openCredentialModal('add', { env })}
            >
              <Icon name="add" slot="prefix" />
              Add Credential
            </Button>
          )}
        </Stack>
      </Stack>

      <Spacer size="xlarge" />

      {/*
        Wrapper is load-bearing: the modals below portal themselves out while open,
        so a branch swap at their sibling position throws in the DOM. See
        portaled-modal-sibling.test.tsx.
      */}
      <div>
        {!hasLoaded ? (
          <SettingsSectionLoader testId="ps-reporting-api-credentials-loading" />
        ) : rows.length === 0 ? (
          <SettingsEmptyState
            testId="ps-reporting-api-credentials-empty"
            title="No API credentials yet"
            description={`Add a credential to pull ${env === 'live' ? 'Live' : 'Sandbox'} conversation data through the Reporting API.`}
          />
        ) : (
          <DataTable
            test-id="ps-reporting-api-credentials-table"
            filterColumns={['name']}
            size="small"
            columns={columns}
            data={rows}
            actionable={false}
          />
        )}
      </div>

      {reportingApiCredentialModal}
      {deleteReportingApiCredentialModal}
    </PageSection>
  );
};
